import React, { useEffect, useState } from "react";
import { X, Upload } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getMealDetailById,
  addMealimg,
  fetchCategory,
  fetchCountry,
  fetchMealDetail,
} from "../../redux/slices/gallerySlice";
import Swal from "sweetalert2";

const EditMealModal = ({ isOpen, onClose, mealId }) => {
  const dispatch = useDispatch();

  const { mealListById, countryList, categoryList, currentPage } = useSelector(
    (state) => state.gallery
  );

  const [loading, setLoading] = useState(false); // 🔥 spinner loader

  const [form, setForm] = useState({
    image: null,
    old_image: "",
    country_id: "",
    category_master_id: "",
    priority: "",
    name: "",
    meal_image_gallery_id: "",
  });

  useEffect(() => {
    dispatch(fetchCountry());
    dispatch(fetchCategory());
  }, [dispatch]);

  useEffect(() => {
    if (isOpen && mealId) {
      dispatch(getMealDetailById(mealId));
    }
  }, [isOpen, mealId, dispatch]);

  useEffect(() => {
    if (mealListById && Object.keys(mealListById).length) {
      setForm({
        image: null,
        old_image: mealListById.image,
        country_id: String(mealListById.country_id || ""),
        category_master_id: String(mealListById.category_master_id || ""),
        priority: String(mealListById.priority || ""),
        name: mealListById.name || "",
        meal_image_gallery_id: mealListById.id || "",
      });
    }
  }, [mealListById]);

  const handleSubmit = async () => {
    if (!form.name || !form.country_id || !form.category_master_id) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        meal_image_gallery_id: form.meal_image_gallery_id,
        name: form.name,
        priority: form.priority,
        country_id: form.country_id,
        category_master_id: form.category_master_id,
        image: form.image ? form.image : "", // ⭐ KEY LINE
      };

      const res = await dispatch(addMealimg(payload)).unwrap();

      if (res.success) {
        Swal.fire("Success", "Meal updated successfully", "success");
        dispatch(fetchMealDetail({ page: currentPage }));
        onClose();
      } else {
        Swal.fire("Error", res.message || "Update failed", "error");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Update failed", "error");
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"></div>

      <div className="fixed inset-0 flex justify-center items-center z-50 px-4">
        <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg">
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-xl font-semibold">Update Meal Image</h2>
            <button onClick={onClose}>
              <X size={22} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Image Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center flex flex-col justify-center items-center hover:border-orange-400 transition relative">
              <Upload size={40} className="text-gray-400 mb-3" />

              <input
                type="file"
                id="editimage"
                className="hidden"
                onChange={(e) => setForm({ ...form, image: e.target.files[0] })}
              />

              <label
                htmlFor="editimage"
                className="text-sm text-orange-500 font-medium cursor-pointer hover:underline"
              >
                Click to change image
              </label>

              {form.image ? (
                <p>{form.image.name}</p>
              ) : (
                <img
                  src={form.old_image}
                  alt={form.image.name}
                  className="w-32 h-32 object-cover mx-auto"
                />
              )}
            </div>

            {/* Country */}
            <select
              name="country_id"
              value={form.country_id}
              onChange={(e) => setForm({ ...form, country_id: e.target.value })}
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select country</option>
              {countryList.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.country_name}
                </option>
              ))}
            </select>

            {/* Category */}
            <select
              name="category_master_id"
              value={form.category_master_id}
              onChange={(e) =>
                setForm({ ...form, category_master_id: e.target.value })
              }
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select Category</option>
              {categoryList.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
            </select>

            {/* Priority */}
            <select
              name="priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select priority</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>

            {/* Name */}
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="p-3 border rounded-md w-full"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <button onClick={onClose} className="px-5 py-2 border rounded-md">
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 rounded-md bg-orange-500 text-white flex items-center gap-2"
            >
              {loading && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {loading ? "" : "Update"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditMealModal;
